import { getTickets as getTicketsApi } from "@/api/customerTicket";
import MessageInputBox from "@/components/Customer/Ticket/MessageInputBox";
import MessageList from "@/components/Customer/Ticket/MessageList";
import { TicketInput } from "@/components/Customer/Ticket/TicketInput";
import { TicketList } from "@/components/Customer/Ticket/TicketList";
import { TicketSearch } from "@/components/Customer/Ticket/TicketSearch";
import { TabStatus, Ticket } from "@/components/Customer/Ticket/types";
import SubHeader from "@/components/SubHeader";
import * as Config from "@/config";
import { customerInfo } from "@/containers/customer/Ticket/types";
import { axios } from "@/lib/axios";
import { useLoadingStore } from "@/stores/loading";
import { useMessageModalStore } from "@/stores/message";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useProjectStore } from "@/stores/project";

type Props = {
  isType: CustomerTicketPageType;
};

export enum CustomerTicketPageType {
  List = "list",
  New = "new",
  Detail = "detail",
  Search = "search",
}
export default function CustomerTicket({ isType }: Props) {
  const params = useParams();
  const setLoading = useLoadingStore((state) => state.setLoading);
  const setMessage = useMessageModalStore((state) => state.setMessage);
  const id = params.customerId;
  const [
    selectedProject,
    selectedProjectCategory,
  ] = useProjectStore(state => [
    state.project,
    state.projectCategory,
  ]);

  const isDetail = isType === CustomerTicketPageType.Detail;
  const isSearch = isType === CustomerTicketPageType.Search;
  const isNew = isType === CustomerTicketPageType.New;

  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const currentStatus = searchParams.get("status");

  const [customerInfo, setCustomerInfo] = useState<customerInfo[]>([]);
  const [customerNumber, setCustomerNumber] = useState<string>("");
  const [groupName, setGroupName] = useState<string>("");
  const [stationName, setStationName] = useState<string>("");
  const [departmentName, setDepartmentName] = useState<string>("");
  const [sectionName, setSectionName] = useState<string>("");
  const [unit_name, setUnitName] = useState<string>("");
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [tabStatuses, setTabStatuses] = useState<TabStatus[] | null>(null);

  const breadCrumbs = [
    { title: selectedProject?.name },
    { title: selectedProjectCategory?.name, path: "/customer" },
    { title: "基本情報" },
  ];


  const modifiedBy = `${customerInfo?.[0]?.modified_by ?? ""}`;
  const modified = `${customerInfo?.[0]?.modified ?? ""}`;
  const getTicket = useCallback(() => {
    setLoading(true);
    getTickets();
  }, []);

  const currentTicket = useMemo(() => {
    if (!isDetail) return;
    return tickets.find((t) => String(t.data_ticket_id) === ticketId);
  }, [ticketId, tickets]);

  const getTickets = useCallback(() => {
    if (!id) return;
    getTicketsApi({ customer_id: Number(id), tab_status: currentStatus })
      .then((result) => {
        setTickets(result.data.data);
        setTabStatuses(result.data.tab_statuses);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, searchParams]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      getTickets();
    }
  }, [id, searchParams, ticketId, isType]);

  useEffect(() => {
    axios
      .get("/api/v1/customers/getCustomer", {
        params: {
          id: id,
        },
      })
      .then((result) => {
        setCustomerInfo(result.data.customer);
        if (result.data.customer[0]["customer_number"] != null) {
          setCustomerNumber(result.data.customer[0]["customer_number"]);
          setGroupName(result.data.customer[0]["group_name"]);
          setStationName(result.data.customer[0]["station_name"]);
          setDepartmentName(result.data.customer[0]["department_name"]);
          setSectionName(result.data.customer[0]["section_name"]);
          setUnitName(result.data.customer[0]["unit_name"]);
        }
      })
      .catch(() => {
        setMessage(Config.MESSAGE_NO_E39);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const dispName = customerNumber + ' ' + groupName
    + (stationName !== '' && stationName !== null ? ' ' + stationName : '')
    + (departmentName !== '' && departmentName !== null ? ' ' + departmentName : '')
    + (sectionName !== '' && sectionName !== null ? ' ' + sectionName : '')
    + (unit_name !== '' && unit_name !== null ? ' ' + unit_name : '');

  return (
    <div>
      <div className="sub-header">
        <SubHeader
          breadCrumbs={[
            { title: selectedProject?.name ?? "" },
            selectedProjectCategory?.id === Config.PROJECT_CATEGORY_ID_FAQ
              ? { title: selectedProjectCategory?.name ?? "", path: "/progress?project_category_id=" + selectedProjectCategory?.id }
              : { title: selectedProjectCategory?.name ?? "", path: "/customer" },
            { title: "DRM-Chat" },
          ]}
          title={`${dispName}`}
        />
        <ul className="uk-tab uk-margin-small-top">
          <li>
            <Link to={`/customer/drmpf/api/${id}`}>利用料</Link>
          </li>
          <li>
            <Link to={`/customer/basicinfo/detail/${id}`}>基本情報</Link>
          </li>
          <li>
            <Link to={`/customer/drmpf/detail/${id}`}>DRM-PF</Link>
          </li>
          <li className="uk-active">
            <Link to={`/customer/ticket/${id}`} style={{ textTransform: 'none' }} >DRM-Chat</Link>
          </li>
          <div className="right">
            最終更新：{modifiedBy}&emsp;日時：{modified}
          </div>
        </ul>
      </div>
      <div className="container ticket-container">
        {isNew ? (
          <TicketInput />
        ) : isSearch ? (
          <TicketSearch />
        ) : (
          <div className="ticket-detail-container">
            {isDetail ? (
              <MessageList
                ticket={currentTicket}
                isDetail={isDetail}
                getTicket={getTicket}
              />
            ) : (
              tickets?.map((t) => (
                <MessageList
                  ticket={t}
                  key={`ticket-message-list-${t.data_ticket_id}`}
                  getTicket={getTicket}
                />
              ))
            )}
            {isDetail && tabStatuses && (
              <MessageInputBox
                ticket={currentTicket}
                getTicket={getTicket}
                customer={customerInfo[0]}
                tabStatuses={tabStatuses}
                key={`ticket-message-input-box-${currentTicket?.data_ticket_id}`}
              />
            )}
          </div>
        )}
        <div className="ticket-list-container">
          {tabStatuses && (
            <TicketList tickets={tickets} tabStatuses={tabStatuses} />
          )}
        </div>
      </div>
    </div>
  );
}
